import React, { useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import { t } from 'ttag'
import { UserCompanyDto, UserProfileDto } from '../../services/api.types'
import { getFullName } from '../../common/helpers/translations'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'
import { Card } from '../../components/Card/Card'
import YumHeader from '../../components/YumHeader/YumHeader'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumChatView from '../../components/YumChatView/YumChatView'
import { useChatChannel, ChatChannelKind } from '../../components/YumChatView/useChatChannel'
import ChatListWrapper from './components/ChatList/ChatListWrapper'
import { entryCid } from './hooks/useChatChannelList'
import ChatHeader from './components/ChatHeader'

import './Chat.scss'

export interface IChatStateProps {
  channelKind: string | null
  channelId: string | null
  userDto: UserProfileDto
  userCompanies: UserCompanyDto[]
}

export default function Chat(props: IChatStateProps) {
  const { channelKind, channelId, userDto, userCompanies } = props

  const { userId, firstName, lastName, email } = userDto
  const name = getFullName(firstName, lastName, email)

  const isValidKind = channelKind === 'challenge' || channelKind === 'group'
  const open = isValidKind && !!channelId
  const kind = open ? (channelKind as ChatChannelKind) : undefined

  const { chatClient, currentChannel, error } = useChatChannel(userId, name, kind, open ? channelId! : undefined)

  // Show the app's standard back button while a conversation is open on mobile; it navigates
  // back to the channel list (see getLogoHref). On desktop the list stays visible next to the
  // conversation, so no back button is needed.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 991px)')
    const apply = () => {
      if (open && mediaQuery.matches) {
        document.body.classList.add('show-back-btn')
      } else {
        document.body.classList.remove('show-back-btn')
      }
    }
    apply()
    mediaQuery.addEventListener?.('change', apply)
    return () => {
      mediaQuery.removeEventListener?.('change', apply)
      document.body.classList.remove('show-back-btn')
    }
  }, [open])

  // The chat view is gated by the GROUP_CHAT company feature.
  if (userCompanies?.length && !hasFeatureFlag(userCompanies, 'groupChat')) {
    return <Redirect to="/notifications" />
  }

  if (channelKind && !isValidKind) {
    return <Redirect to="/chat" />
  }

  return (
    <Card className="chat-card">
      <NotificationsWrapper />
      <div className={`chat-view${open ? ' chat-view--open' : ''}`}>
        <div className="chat-view__list">
          {/* The title lives in the list column so the conversation pane can use the full card
              height on desktop. On mobile it hides together with the list when a conversation
              is open — there the channel header provides the context. */}
          <YumHeader>
            <h1>{t`Chat`}</h1>
          </YumHeader>
          <ChatListWrapper activeCid={open ? entryCid({ kind: kind!, id: channelId!, name: '' }) : undefined} />
        </div>
        {open ? (
          <div className="chat-view__conversation">
            <YumChatView
              chatClient={chatClient}
              currentChannel={currentChannel}
              error={error}
              header={<ChatHeader kind={kind!} />}
            />
          </div>
        ) : (
          <div className="chat-view__placeholder">{t`Select a conversation`}</div>
        )}
      </div>
    </Card>
  )
}
